import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root                                                                 */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  padding: ${sizes.spacing[48]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
    padding: ${sizes.spacing[64]}px 0;
  }

  .MuiTabs-root {
    width: 100%;
    max-width: 100%;
  }
`

/* ------------------------------------------------------------------ */
/* Header — title row with chevron + title                             */
/* ------------------------------------------------------------------ */
export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
  width: 100%;
`

export const Title = styled(Typography)`
  color: ${colors.light.text.primary};
`

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const Subtitle = styled(Typography)`
  color: ${colors.light.text.primary};
  width: 100%;
`

/* Category tabs — uses MUI Tabs with theme overrides (theme.ts MuiTabs/MuiTab) */

export const CategoryScrollButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid ${colors.primitives.secondary500};
  background-color: ${colors.primitives.neutral0};
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  /* EGDE-496: same hover pairing as the main-image arrows. */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral100};
    border-color: ${colors.primitives.neutral500};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

/* ------------------------------------------------------------------ */
/* Main image                                                          */
/* ------------------------------------------------------------------ */
/* EGDE-109: groups the main image with its mobile caption so the image→caption
   gap is the design's 24px instead of inheriting the Root's 40px section gap,
   which QA flagged as "too much gap" on mobile. Design spacing on mobile:
   tabs→image 40 (Root gap), image→caption 24 (this group), caption→thumbnails
   40 (Root gap). On desktop the caption is overlaid on the image (MobileCaption
   is hidden), so the group has a single child and the gap is inert. */
export const MainImageGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
`

export const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 343 / 218;
  border-radius: ${sizes.borderRadius[16]}px;
  overflow: hidden;

  @media (min-width: ${breakpoints.lg}px) {
    aspect-ratio: 1200 / 764;
    border-radius: ${sizes.borderRadius[24]}px;
  }
`

/* EGDE-602: the gallery honours the responsiveImage Mobile Image. Toggled in CSS
   rather than with a viewport hook so the server and client markup match — same
   pattern as the inner hero and the testimonial avatar (EGDE-597/599). Shared by
   the main image and the thumbnail strip, which must agree on which crop is
   showing (EGDE-602 r2, QA). */
export const DesktopOnlyLayer = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const MobileOnlyLayer = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const MainImageOverlay = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    position: absolute;
    inset: 0;
    border-radius: ${sizes.borderRadius[24]}px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 52.5%,
      rgba(0, 0, 0, 1) 100%
    );
    z-index: 1;
    pointer-events: none;
  }
`

export const ImageCounterBadge = styled.div`
  position: absolute;
  z-index: 2;
  left: ${sizes.spacing[16]}px;
  top: ${sizes.spacing[16]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${sizes.spacing[6]}px ${sizes.spacing[12]}px;
  border-radius: ${sizes.borderRadius.full}px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(4px);

  @media (min-width: ${breakpoints.lg}px) {
    left: ${sizes.spacing[32]}px;
    top: ${sizes.spacing[32]}px;
  }
`

export const CounterText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
`

/* Desktop caption — overlaid on the main image bottom */
export const DesktopCaption = styled(Typography)`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    position: absolute;
    bottom: ${sizes.spacing[32]}px;
    left: ${sizes.spacing[32]}px;
    right: ${sizes.spacing[32]}px;
    z-index: 2;
    color: ${colors.primitives.neutral0};
    font-weight: 700 !important;
    text-align: start;
  }
`

/* Mobile caption — below the image */
export const MobileCaption = styled(Typography)`
  color: ${colors.light.text.primary};
  width: 100%;
  font-weight: 700 !important;
  text-align: center;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* ------------------------------------------------------------------ */
/* Navigation arrows — desktop only, on main image                     */
/* ------------------------------------------------------------------ */
export const NavArrowsContainer = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    position: absolute;
    top: 50%;
    /* @noflip */
    left: ${sizes.spacing[32]}px;
    /* @noflip */
    right: ${sizes.spacing[32]}px;
    transform: translateY(-50%);
    z-index: 2;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;
  }
`

export const NavArrowButton = styled.button`
  width: 46px;
  height: 46px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid ${colors.primitives.secondary500};
  background-color: ${colors.primitives.neutral0};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  flex-shrink: 0;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  /* EGDE-496: neutral100 alone reads as "no hover" on white — pair it with a
     darker ring like the strip pause button. */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral100};
    border-color: ${colors.primitives.neutral500};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

/* ------------------------------------------------------------------ */
/* Thumbnail strip                                                     */
/* ------------------------------------------------------------------ */
export const ThumbnailStripWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[12]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[24]}px;
  }
`

export const ThumbnailSwiperContainer = styled.div`
  flex: 1;
  overflow: hidden;
  min-width: 0;

  .swiper {
    width: 100%;
    /* EGDE-109: cancel the global .swiper 50px top/bottom padding (GlobalStyle.ts)
       — it made the thumbnail strip ~100px taller than the thumbnails and centered
       them, leaving a big empty band between the caption and the thumbnails. Hero
       and Promotional banners override the same global padding. */
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .swiper-slide {
    width: auto;
  }
`

export const ThumbnailNavButton = styled.button`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: ${sizes.borderRadius.full}px;
    border: 1px solid ${colors.primitives.secondary500};
    background-color: ${colors.primitives.neutral0};
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${colors.primitives.neutral100};
    }
  }
`

export const ThumbnailItem = styled.button<{ $selected: boolean }>`
  flex-shrink: 0;
  position: relative;
  width: 142px;
  height: 90px;
  border-radius: ${sizes.borderRadius[12]}px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid
    ${(p) => (p.$selected ? colors.primitives.secondary500 : "transparent")};
  padding: 0;
  background: none;
  opacity: ${(p) => (p.$selected ? 1 : 0.6)};
  transition: opacity 0.2s ease, border-color 0.2s ease;

  @media (min-width: ${breakpoints.lg}px) {
    width: 289px;
    height: 184px;
    border-radius: ${sizes.borderRadius[16]}px;
  }
`

export const ThumbnailImage = styled.div`
  position: absolute;
  inset: 0;
`

/* ------------------------------------------------------------------ */
/* Video                                                               */
/* ------------------------------------------------------------------ */
export const MainVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`

export const ThumbnailVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const VideoPlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  color: ${colors.primitives.neutral0};
  font-size: 24px;
  pointer-events: none;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 32px;
  }
`
